import { createIsland } from 'preact-island';
import type { Island } from 'preact-island';

import { WidgetWrapper } from '../../components/WidgetWrapper';
import type { WidgetTheme } from '../../types/theme';

import { websocketConnectionManager } from '../websocketConnectionManager';

interface WidgetWrapperProps {
  widgetTitle: string;
  companyLogoSrc?: string;
  widgetUuid?: string;
  widgetTheme?: WidgetTheme;
  entryPointGateway?: string;
  requirePhone?: boolean;
  panelOffsetTop?: number;
  hideTriggerButton?: boolean;
  widgetLocale?: string;
}

// Todo добавить отписывание от событий
class WidgetManager {
  // CustomEvent для document.addEventListener
  static readonly DISPATCH_OPEN = 'vks-widget-open';
  static readonly DISPATCH_CLOSE = 'vks-widget-close';

  private islandInstance: Island<WidgetWrapperProps> | null = null;
  private isMounted: boolean = false;
  private currentUrl: string = window.location.origin;
  private currentPathname: string = window.location.pathname;
  private selector: string = '[data-island="widget-chat"]';
  private widgetVisibilityCallback: ((isVisible: boolean) => void) | null | undefined = null;
  private externalVisibilityCallback: ((isVisible: boolean) => void) | null | undefined = null;
  private lastNotifiedVisible: boolean | null = null;

  constructor() {
    this.initHistoryListener();
    this.initDataAttributeListener();
  }

  init(): void {
    this.checkAndMountWidget();
  }

  setVisibilityCallback(callback: ((isVisible: boolean) => void) | undefined): void {
    this.externalVisibilityCallback = callback;
  }

  setInternalVisibilityCallback(callback: ((isVisible: boolean) => void) | undefined): void {
    this.widgetVisibilityCallback = callback;
  }

  notifyVisibilityChange(isVisible: boolean): void {
    if (this.externalVisibilityCallback) {
      this.externalVisibilityCallback(isVisible);
    }
    if (this.lastNotifiedVisible !== isVisible) {
      this.lastNotifiedVisible = isVisible;
      const eventName = isVisible ? WidgetManager.DISPATCH_OPEN : WidgetManager.DISPATCH_CLOSE;
      document.dispatchEvent(new CustomEvent(eventName, { bubbles: true }));
    }
  }

  openWidget(): void {
    if (this.widgetVisibilityCallback) {
      this.widgetVisibilityCallback(true);
    }
    this.notifyVisibilityChange(true);
  }

  closeWidget(): void {
    if (this.widgetVisibilityCallback) {
      this.widgetVisibilityCallback(false);
    }
    this.notifyVisibilityChange(false);
  }

  private initDataAttributeListener(): void {
    // Обработчик кликов для элементов с data-vks-open-widget
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const openWidgetElement = target.closest('[data-vks-open-widget]');

      if (openWidgetElement) {
        event.preventDefault();
        this.openWidget();
      }
    });
  }

  private checkAndMountWidget(): void {
    const container = document.querySelector(this.selector);

    if (container && !this.isMounted) {
      this.islandInstance = createIsland(WidgetWrapper);
      this.islandInstance?.render({
        selector: this.selector,
      });
      this.isMounted = true;
    }
  }

  private unmountWidget(): void {
    if (this.islandInstance && this.isMounted) {
      // Закрываем все WebSocket соединения перед размонтированием
      void websocketConnectionManager.closeAllConnections();

      this.islandInstance.destroy();
      this.isMounted = false;
    }
  }

  private initHistoryListener(): void {
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      this.handleRouteChange();
    };

    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      this.handleRouteChange();
    };

    window.addEventListener('popstate', () => {
      this.handleRouteChange();
    });
  }

  private handleRouteChange(): void {
    const newUrl = window.location.origin;
    const newPathname = window.location.pathname;

    if (newPathname !== this.currentPathname) {
      this.closeWidget();
    }

    if (newUrl !== this.currentUrl) {
      this.currentUrl = newUrl;
      this.unmountWidget();
    }
  }
}

export const widgetManager = new WidgetManager();
